import React, { useEffect, useState } from "react";
import {
  EditTwoTone,
  LinkOutlined,
  FileTextOutlined,
  SafetyCertificateOutlined,
  UploadOutlined,
  DeleteOutlined,
  PictureOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import {
  Button,
  Descriptions,
  Form,
  Image,
  Input,
  Select,
  Space,
  Tag,
  Upload,
  theme,
} from "antd";
import PracticesService, {
  IPracticeAddress,
  IPracticeBasicInformationInitialValues,
  PracticeUpdateTypes,
} from "../../Services/PracticesService";
import { Utils } from "../../Utilities/Utils";
import { adminContext } from "../../Common/PageRoute";
import { useUser } from "../../Context/UserContext";
import { AppDrawer } from "../../Common/Layout";
import BankAccount from "./BankAccount";

interface BasicInformationProps {
  isEdit: { type: PracticeUpdateTypes };
  setIsEdit: React.Dispatch<
    React.SetStateAction<{ type: PracticeUpdateTypes }>
  >;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  practiceId: number;
  refreshTrigger: number;
  onBasicInfoUpdated?: (data: any) => void;
}

const BasicInformation: React.FC<BasicInformationProps> = ({
  isEdit,
  setIsEdit,
  setLoading,
  practiceId,
  refreshTrigger,
  onBasicInfoUpdated,
}) => {
  const { countriesOptions, openNotification } = React.useContext(adminContext);
  const { isSubAdmin } = useUser();
  const isViewOnly = isSubAdmin();
  const { token } = theme.useToken();

  const [basicForm] = Form.useForm();
  const [addressForm] = Form.useForm();
  const [data, setData] = useState<any>(null);
  const [savingBasic, setSavingBasic] = useState(false);
  const [savingAddress, setSavingAddress] = useState(false);
  const [previewImage, setPreviewImage] = useState<string | null>(null);

  const getBasicInformation = async () => {
    try {
      setLoading(true);
      const res =
        await PracticesService.getPracticeBasicInformation(practiceId);
      if (res?.status && res?.result) {
        setData(res.result);
        basicForm.setFieldsValue({
          name: res.result.name,
          email: res.result.email,
          phoneNumber: res.result.phoneNumber,
          website: res.result.website,
        });
        addressForm.setFieldsValue({
          address: res.result.address,
        });
        onBasicInfoUpdated?.(res.result);
      } else {
        openNotification(
          "error",
          res?.message || "Error fetching basic information",
        );
      }
    } catch {
      openNotification("error", "Error fetching basic information");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (practiceId) {
      getBasicInformation();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [practiceId, refreshTrigger]);

  const isEditingBasic = isEdit.type === PracticeUpdateTypes.basicInformation;
  const isEditingAddress = isEdit.type === PracticeUpdateTypes.address;

  const handleOpenBasicEdit = () => {
    if (data) {
      basicForm.setFieldsValue({
        name: data.name,
        email: data.email,
        phoneNumber: data.phoneNumber,
        website: data.website,
      });
    }
    setIsEdit({ type: PracticeUpdateTypes.basicInformation });
  };

  const handleOpenAddressEdit = () => {
    if (data?.address) {
      addressForm.setFieldsValue({
        address: data.address,
      });
    }
    setIsEdit({ type: PracticeUpdateTypes.address });
  };

  const handleCancel = () => {
    setIsEdit({ type: PracticeUpdateTypes.unknown });
  };

  const saveBasicInfo = async () => {
    try {
      const values = await basicForm.validateFields();
      setSavingBasic(true);
      setLoading(true);

      const params: IPracticeBasicInformationInitialValues = {
        name: values.name,
        email: values.email,
        phoneNumber: values.phoneNumber,
        website: values.website,
      };

      const res = await PracticesService.updatePracticeBasicInformation(
        practiceId,
        params,
      );

      if (res?.result) {
        openNotification("success", "Basic information updated successfully");
        setIsEdit({ type: PracticeUpdateTypes.unknown });
        await getBasicInformation();
      } else {
        openNotification(
          "error",
          res?.message || "Error updating basic information",
        );
      }
    } catch {
      openNotification("error", "Please fill all required fields properly");
    } finally {
      setSavingBasic(false);
      setLoading(false);
    }
  };

  const saveAddressInfo = async () => {
    try {
      const values = await addressForm.validateFields();
      setSavingAddress(true);
      setLoading(true);

      const params: IPracticeAddress = values.address;
      const res = await PracticesService.updatePracticeAddressInformation(
        practiceId,
        params,
      );

      if (res?.result) {
        openNotification("success", "Address details updated successfully");
        setIsEdit({ type: PracticeUpdateTypes.unknown });
        await getBasicInformation();
      } else {
        openNotification(
          "error",
          res?.message || "Error updating address details",
        );
      }
    } catch {
      openNotification("error", "Please fill all required fields properly");
    } finally {
      setSavingAddress(false);
      setLoading(false);
    }
  };

  const handleImageUpload = (fieldName: "iconUrl" | "logoUrl", file: File) => {
    const isImage =
      file.type.startsWith("image/") ||
      file.name.endsWith(".ico") ||
      file.name.endsWith(".svg");
    if (!isImage) {
      openNotification(
        "error",
        "You can only upload image files (PNG, JPG, SVG, ICO).",
      );
      return false;
    }
    const isLt5M = file.size / 1024 / 1024 < 5;
    if (!isLt5M) {
      openNotification("error", "Image must be smaller than 5MB.");
      return false;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      const dataUrl = e.target?.result as string;
      basicForm.setFieldValue(["website", fieldName], dataUrl);
    };
    reader.readAsDataURL(file);
    return false;
  };

  const validateUrl = (_: any, value: string) => {
    if (!value) return Promise.resolve();
    if (
      !value.startsWith("https://") &&
      !value.startsWith("http://") &&
      !value.startsWith("data:image/")
    ) {
      return Promise.reject(
        new Error("Please enter a valid URL (starting with https://)"),
      );
    }
    return Promise.resolve();
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 16,
      }}
    >
      {/* 1. Basic Information Section */}
      <div
        style={{
          background: token.colorBgContainer,
          borderRadius: token.borderRadiusLG,
          padding: "16px 20px",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            paddingBottom: 12,
            marginBottom: 14,
            borderBottom: `1px solid ${token.colorBorderSecondary}`,
          }}
        >
          <div>
            <span
              style={{
                fontSize: 14,
                fontWeight: 600,
                color: token.colorTextHeading,
              }}
            >
              Basic Information
            </span>
            <div
              style={{
                fontSize: 11,
                color: token.colorTextSecondary,
                marginTop: 1,
              }}
            >
              Contact details, main website URL, legal policies, and branding
              assets.
            </div>
          </div>

          {!isViewOnly && (
            <Button
              size="small"
              type="text"
              shape="circle"
              icon={<EditTwoTone />}
              onClick={handleOpenBasicEdit}
            />
          )}
        </div>

        <Descriptions
          size="small"
          bordered={false}
          column={{ xs: 1, sm: 2, md: 3 }}
          labelStyle={{
            color: token.colorTextSecondary,
            fontWeight: 500,
            fontSize: 12,
          }}
          contentStyle={{
            color: token.colorText,
            fontWeight: 500,
            fontSize: 13,
          }}
        >
          <Descriptions.Item label="Contact Name">
            {data?.name || "—"}
          </Descriptions.Item>

          <Descriptions.Item label="Email">
            {data?.email ? (
              <a href={`mailto:${data.email}`}>{data.email}</a>
            ) : (
              "—"
            )}
          </Descriptions.Item>

          <Descriptions.Item label="Phone Number">
            {data?.phoneNumber ? (
              <a href={`tel:${data.phoneNumber}`}>{data.phoneNumber}</a>
            ) : (
              "—"
            )}
          </Descriptions.Item>

          <Descriptions.Item label="Website URL">
            {data?.website?.url ? (
              <a
                href={data.website.url}
                target="_blank"
                rel="noopener noreferrer"
                style={{ display: "inline-flex", alignItems: "center" }}
              >
                <Tag
                  color="blue"
                  icon={<LinkOutlined />}
                  style={{ borderRadius: 4, cursor: "pointer", margin: 0 }}
                >
                  Visit Website
                </Tag>
              </a>
            ) : (
              "—"
            )}
          </Descriptions.Item>

          <Descriptions.Item label="T&C URL">
            {data?.website?.urlTnC ? (
              <a
                href={data.website.urlTnC}
                target="_blank"
                rel="noopener noreferrer"
                style={{ display: "inline-flex", alignItems: "center" }}
              >
                <Tag
                  icon={<FileTextOutlined />}
                  style={{ borderRadius: 4, cursor: "pointer", margin: 0 }}
                >
                  View Terms
                </Tag>
              </a>
            ) : (
              "—"
            )}
          </Descriptions.Item>

          <Descriptions.Item label="Privacy Policy URL">
            {data?.website?.urlPrivacy ? (
              <a
                href={data.website.urlPrivacy}
                target="_blank"
                rel="noopener noreferrer"
                style={{ display: "inline-flex", alignItems: "center" }}
              >
                <Tag
                  icon={<SafetyCertificateOutlined />}
                  style={{ borderRadius: 4, cursor: "pointer", margin: 0 }}
                >
                  View Privacy Policy
                </Tag>
              </a>
            ) : (
              "—"
            )}
          </Descriptions.Item>

          <Descriptions.Item label="Favicon Icon">
            {data?.website?.iconUrl ? (
              <Button
                type="text"
                shape="circle"
                size="small"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  height: 22,
                  width: 22,
                  minWidth: 22,
                  verticalAlign: "middle",
                }}
                icon={
                  <EyeOutlined
                    style={{ color: token.colorPrimary, fontSize: 15 }}
                  />
                }
                onClick={() => setPreviewImage(data.website.iconUrl)}
                title="Preview Favicon Icon"
              />
            ) : (
              "—"
            )}
          </Descriptions.Item>

          <Descriptions.Item label="Practice Logo">
            {data?.website?.logoUrl ? (
              <Button
                type="text"
                shape="circle"
                size="small"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  height: 22,
                  width: 22,
                  minWidth: 22,
                  verticalAlign: "middle",
                }}
                icon={
                  <EyeOutlined
                    style={{ color: token.colorPrimary, fontSize: 15 }}
                  />
                }
                onClick={() => setPreviewImage(data.website.logoUrl)}
                title="Preview Practice Logo"
              />
            ) : (
              "—"
            )}
          </Descriptions.Item>
        </Descriptions>

        {previewImage && (
          <Image
            wrapperStyle={{ display: "none" }}
            src={previewImage}
            preview={{
              visible: !!previewImage,
              onVisibleChange: (visible) => {
                if (!visible) setPreviewImage(null);
              },
            }}
          />
        )}
      </div>

      {/* 2. Address Details Section */}
      <div
        style={{
          background: token.colorBgContainer,
          borderRadius: token.borderRadiusLG,
          padding: "16px 20px",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            paddingBottom: 12,
            marginBottom: 14,
            borderBottom: `1px solid ${token.colorBorderSecondary}`,
          }}
        >
          <div>
            <span
              style={{
                fontSize: 14,
                fontWeight: 600,
                color: token.colorTextHeading,
              }}
            >
              Address Details
            </span>
            <div
              style={{
                fontSize: 11,
                color: token.colorTextSecondary,
                marginTop: 1,
              }}
            >
              Registered business premises and geographic location.
            </div>
          </div>

          {!isViewOnly && (
            <Button
              size="small"
              type="text"
              shape="circle"
              icon={<EditTwoTone />}
              onClick={handleOpenAddressEdit}
            />
          )}
        </div>

        <Descriptions
          size="small"
          bordered={false}
          column={{ xs: 1, sm: 2, md: 3 }}
          labelStyle={{
            color: token.colorTextSecondary,
            fontWeight: 500,
            fontSize: 12,
          }}
          contentStyle={{
            color: token.colorText,
            fontWeight: 500,
            fontSize: 13,
          }}
        >
          <Descriptions.Item label="Building">
            {data?.address?.building || "—"}
          </Descriptions.Item>

          <Descriptions.Item label="Street">
            {data?.address?.street || "—"}
          </Descriptions.Item>

          <Descriptions.Item label="City / Town">
            {data?.address?.city || "—"}
          </Descriptions.Item>

          <Descriptions.Item label="County">
            {data?.address?.county || "—"}
          </Descriptions.Item>

          <Descriptions.Item label="Postcode">
            {data?.address?.postcode ? (
              <Tag color="geekblue" style={{ borderRadius: 4, margin: 0 }}>
                {data.address.postcode}
              </Tag>
            ) : (
              "—"
            )}
          </Descriptions.Item>

          <Descriptions.Item label="Country">
            {countriesOptions?.find(
              (c: any) => c.value === data?.address?.country,
            )?.label ||
              data?.address?.country ||
              "—"}
          </Descriptions.Item>
        </Descriptions>
      </div>

      {/* 3. Primary Bank Account */}
      <BankAccount data={data?.bankAccount} />

      {/* Basic Information Edit Drawer */}
      <AppDrawer
        title="Edit Basic Information"
        subtitle="Update contact details, website, and policy links"
        width={480}
        open={isEditingBasic}
        onClose={handleCancel}
        onSave={saveBasicInfo}
        saveText="Save Changes"
        saveButtonProps={{ loading: savingBasic }}
        cancelButtonProps={{ disabled: savingBasic }}
      >
        <Form form={basicForm} layout="vertical" disabled={savingBasic}>
          <Form.Item
            label="Name"
            name="name"
            rules={[
              { required: true, message: "Name is required" },
              { validator: Utils.validateWhiteSpace },
            ]}
          >
            <Input placeholder="Name" />
          </Form.Item>

          <Form.Item
            label="Email"
            name="email"
            rules={[
              { required: true, message: "Email is required" },
              { validator: Utils.ValidateEmailFormat },
            ]}
          >
            <Input placeholder="Email" />
          </Form.Item>

          <Form.Item
            label="Phone Number"
            name="phoneNumber"
            rules={[{ required: true, message: "Phone number is required" }]}
          >
            <Input
              onKeyPress={(e) => Utils.ValidateNumberOnly(e)}
              maxLength={13}
              placeholder="Phone Number"
            />
          </Form.Item>

          <Form.Item
            label="Website URL"
            name={["website", "url"]}
            rules={[
              { type: "url", message: "Please enter a valid URL" },
              { validator: validateUrl },
            ]}
          >
            <Input
              prefix={
                <LinkOutlined style={{ color: token.colorTextSecondary }} />
              }
              placeholder="https://www.example.com"
            />
          </Form.Item>

          <Form.Item
            label="T&C URL"
            name={["website", "urlTnC"]}
            rules={[{ validator: validateUrl }]}
          >
            <Input
              prefix={
                <FileTextOutlined style={{ color: token.colorTextSecondary }} />
              }
              placeholder="https://www.example.com/terms"
            />
          </Form.Item>

          <Form.Item
            label="Privacy Policy URL"
            name={["website", "urlPrivacy"]}
            rules={[{ validator: validateUrl }]}
          >
            <Input
              prefix={
                <SafetyCertificateOutlined
                  style={{ color: token.colorTextSecondary }}
                />
              }
              placeholder="https://www.example.com/privacy"
            />
          </Form.Item>

          {/* Favicon Icon with Upload & Real-time Preview */}
          <Form.Item label="Favicon Icon" style={{ marginBottom: 16 }}>
            <Form.Item
              name={["website", "iconUrl"]}
              noStyle
              rules={[{ validator: validateUrl }]}
            >
              <Input
                prefix={
                  <PictureOutlined
                    style={{ color: token.colorTextSecondary }}
                  />
                }
                placeholder="https://.../favicon.ico or upload below"
                style={{ marginBottom: 8 }}
              />
            </Form.Item>
            <Form.Item
              shouldUpdate={(prev, curr) =>
                prev?.website?.iconUrl !== curr?.website?.iconUrl
              }
              noStyle
            >
              {({ getFieldValue }) => {
                const iconUrl = getFieldValue(["website", "iconUrl"]);
                return (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      padding: "8px 12px",
                      background: token.colorFillAlter,
                      borderRadius: token.borderRadiusSM,
                      border: `1px dashed ${token.colorBorder}`,
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                      }}
                    >
                      {iconUrl ? (
                        <Image
                          src={iconUrl}
                          width={32}
                          height={32}
                          style={{
                            objectFit: "contain",
                            borderRadius: 4,
                            background: "#fff",
                            border: `1px solid ${token.colorBorderSecondary}`,
                          }}
                        />
                      ) : (
                        <PictureOutlined
                          style={{
                            fontSize: 24,
                            color: token.colorTextQuaternary,
                          }}
                        />
                      )}
                      <span
                        style={{
                          fontSize: 12,
                          color: token.colorTextSecondary,
                        }}
                      >
                        {iconUrl ? "Icon preview ready" : "No icon uploaded"}
                      </span>
                    </div>
                    <Space size="small">
                      {iconUrl && (
                        <Button
                          size="small"
                          type="text"
                          danger
                          icon={<DeleteOutlined />}
                          onClick={() =>
                            basicForm.setFieldValue(["website", "iconUrl"], "")
                          }
                        >
                          Remove
                        </Button>
                      )}
                      <Upload
                        showUploadList={false}
                        accept="image/*,.ico,.svg"
                        beforeUpload={(file) =>
                          handleImageUpload("iconUrl", file)
                        }
                      >
                        <Button size="small" icon={<UploadOutlined />}>
                          {iconUrl ? "Replace" : "Upload"}
                        </Button>
                      </Upload>
                    </Space>
                  </div>
                );
              }}
            </Form.Item>
          </Form.Item>

          {/* Practice Logo with Upload & Real-time Preview */}
          <Form.Item label="Practice Logo" style={{ marginBottom: 16 }}>
            <Form.Item
              name={["website", "logoUrl"]}
              noStyle
              rules={[{ validator: validateUrl }]}
            >
              <Input
                prefix={
                  <PictureOutlined
                    style={{ color: token.colorTextSecondary }}
                  />
                }
                placeholder="https://.../logo.png or upload below"
                style={{ marginBottom: 8 }}
              />
            </Form.Item>
            <Form.Item
              shouldUpdate={(prev, curr) =>
                prev?.website?.logoUrl !== curr?.website?.logoUrl
              }
              noStyle
            >
              {({ getFieldValue }) => {
                const logoUrl = getFieldValue(["website", "logoUrl"]);
                return (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      padding: "8px 12px",
                      background: token.colorFillAlter,
                      borderRadius: token.borderRadiusSM,
                      border: `1px dashed ${token.colorBorder}`,
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                      }}
                    >
                      {logoUrl ? (
                        <Image
                          src={logoUrl}
                          height={32}
                          style={{
                            maxWidth: 120,
                            objectFit: "contain",
                            borderRadius: 4,
                            background: "#fff",
                            border: `1px solid ${token.colorBorderSecondary}`,
                          }}
                        />
                      ) : (
                        <PictureOutlined
                          style={{
                            fontSize: 24,
                            color: token.colorTextQuaternary,
                          }}
                        />
                      )}
                      <span
                        style={{
                          fontSize: 12,
                          color: token.colorTextSecondary,
                        }}
                      >
                        {logoUrl ? "Logo preview ready" : "No logo uploaded"}
                      </span>
                    </div>
                    <Space size="small">
                      {logoUrl && (
                        <Button
                          size="small"
                          type="text"
                          danger
                          icon={<DeleteOutlined />}
                          onClick={() =>
                            basicForm.setFieldValue(["website", "logoUrl"], "")
                          }
                        >
                          Remove
                        </Button>
                      )}
                      <Upload
                        showUploadList={false}
                        accept="image/*,.svg"
                        beforeUpload={(file) =>
                          handleImageUpload("logoUrl", file)
                        }
                      >
                        <Button size="small" icon={<UploadOutlined />}>
                          {logoUrl ? "Replace" : "Upload"}
                        </Button>
                      </Upload>
                    </Space>
                  </div>
                );
              }}
            </Form.Item>
          </Form.Item>
        </Form>
      </AppDrawer>

      {/* Address Details Edit Drawer */}
      <AppDrawer
        title="Edit Address Details"
        subtitle="Update registered office address information"
        width={480}
        open={isEditingAddress}
        onClose={handleCancel}
        onSave={saveAddressInfo}
        saveText="Save Changes"
        saveButtonProps={{ loading: savingAddress }}
        cancelButtonProps={{ disabled: savingAddress }}
      >
        <Form form={addressForm} layout="vertical" disabled={savingAddress}>
          <Form.Item label="Building" name={["address", "building"]}>
            <Input placeholder="Building name or number" />
          </Form.Item>

          <Form.Item label="Street" name={["address", "street"]}>
            <Input placeholder="Street name" />
          </Form.Item>

          <Form.Item label="City / Town" name={["address", "city"]}>
            <Input placeholder="City or Town" />
          </Form.Item>

          <Form.Item label="County" name={["address", "county"]}>
            <Input
              onKeyPress={(e: any) => Utils.ValidateCharOnly(e)}
              placeholder="County"
            />
          </Form.Item>

          <Form.Item label="Postcode" name={["address", "postcode"]}>
            <Input
              onKeyPress={(e: any) => Utils.ValidateCharAndNumberOnly(e)}
              placeholder="Postcode"
            />
          </Form.Item>

          <Form.Item label="Country" name={["address", "country"]}>
            <Select
              showSearch
              placeholder="Select Country"
              options={countriesOptions}
            />
          </Form.Item>
        </Form>
      </AppDrawer>
    </div>
  );
};

export default BasicInformation;
